import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

// Logo ICL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a3de8ce8-9ebc-47a8-b532-221c9e0f1005/artifacts/0dlj7jep_LOGO%20ICL.png";

const LoginPage = () => {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginData.email, loginData.password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!acceptPrivacy) {
      setError('Debes aceptar la política de privacidad y las condiciones de participación');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await register(registerData.email, registerData.password, registerData.name);
      setSuccess('Cuenta creada. Ahora puedes iniciar sesión.');
      setRegisterData({ email: '', password: '', name: '' });
      setAcceptPrivacy(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding with dark background */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-4 mb-10">
            <img src={LOGO_URL} alt="ICL Logo" className="h-14 w-auto" />
            <div>
              <span className="text-2xl font-bold tracking-tight block">Cumplimiento Laboral</span>
              <span className="text-sky-400 text-xs tracking-widest uppercase">Instituto de Cumplimiento Laboral</span>
            </div>
          </div>
          <h1 className="text-5xl font-bold leading-tight mb-6">
            Gestión integral del<br />cumplimiento<br />normativo laboral
          </h1>
          <p className="text-slate-400 text-lg max-w-md leading-relaxed">
            Controla el grado de cumplimiento de tu empresa con las 12 áreas de compliance laboral español.
          </p>
        </div>
        
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">51 obligaciones verificadas</h3>
              <p className="text-slate-400 text-sm">Basado en el cuestionario SGI Labour Compliance del ICL</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Dashboard en tiempo real</h3>
              <p className="text-slate-400 text-sm">Visualiza el porcentaje de cumplimiento por área</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Auditoría completa</h3>
              <p className="text-slate-400 text-sm">Historial de cambios y documentos de evidencia</p>
            </div>
          </div>
        </div>
        
        <p className="text-slate-500 text-sm">
          © 2026 Cumplimiento Laboral. Instituto de Cumplimiento Laboral (ICL).
        </p>
      </div>

      {/* Right side - Auth forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src={LOGO_URL} alt="ICL Logo" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight">Cumplimiento Laboral</span>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Iniciar sesión</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-slate-200">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl">Bienvenido</CardTitle>
                  <CardDescription>Introduce tus credenciales para acceder</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contraseña</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                    
                    {error && (
                      <div className="flex items-center gap-2 text-rose-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      data-testid="login-submit"
                      className="w-full bg-slate-900 hover:bg-slate-800" 
                      disabled={loading}
                    >
                      {loading ? 'Cargando...' : 'Iniciar sesión'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-slate-200">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl">Crear cuenta</CardTitle>
                  <CardDescription>Completa el formulario para registrarte</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nombre</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name"
                        type="text"
                        placeholder="Tu nombre"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Contraseña</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                      />
                    </div>

                    {/* Información legal RGPD */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 leading-relaxed">
                      <p className="mb-3">
                        <strong>GRUPO DE SISTEMAS GLOBALES DE PREVENCIÓN S.L.</strong> como responsable del tratamiento 
                        tratará sus datos con la finalidad de gestionar el registro y la participación en el cuestionario 
                        Cumplimiento Laboral. Puede acceder, rectificar y suprimir sus datos, así como ejercer otros 
                        derechos consultando la información adicional y detallada sobre protección de datos en nuestra{' '}
                        <a 
                          href="https://compliancelaboral.com/politica-de-privacidad/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:underline font-medium"
                        >
                          Política de Privacidad
                        </a>.
                      </p>
                    </div>

                    {/* Checkbox de aceptación - OBLIGATORIO */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="accept-privacy"
                        data-testid="accept-privacy"
                        checked={acceptPrivacy}
                        onChange={(e) => setAcceptPrivacy(e.target.checked)}
                        required
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                      />
                      <label htmlFor="accept-privacy" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                        <span className="text-rose-500">*</span> He leído y acepto las{' '}
                        <a 
                          href="https://compliancelaboral.com/condiciones/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:underline font-medium"
                        >
                          Condiciones de Participación
                        </a>{' '}
                        en el cuestionario Cumplimiento Laboral y la{' '}
                        <a 
                          href="https://compliancelaboral.com/politica-de-privacidad/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:underline font-medium"
                        >
                          Política de Privacidad
                        </a>{' '}
                        sobre el tratamiento de mis datos para el registro y participación en el cuestionario.
                      </label>
                    </div>
                    
                    {error && (
                      <div className="flex items-center gap-2 text-rose-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}
                    
                    {success && (
                      <div className="flex items-center gap-2 text-emerald-600 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        {success}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      data-testid="register-submit"
                      className="w-full bg-slate-900 hover:bg-slate-800" 
                      disabled={loading}
                    >
                      {loading ? 'Cargando...' : 'Crear cuenta'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
